// Web Storage can throw (Safari private mode, sandboxed iframes, blocked site
// data) — rate limiting must fail open rather than break the form.
//
// It must not fail open *silently*, though. The post-timeout duplicate-send
// guard has no other mechanism: a deadline breach says nothing about whether
// the provider accepted the message, so the cooldown is the only thing between
// a retry and the same message being delivered twice. A dropped write there is
// not a relaxed rate limit, it is a duplicate send.
//
// So a write is offered to each tier in turn, most durable first, and lands in
// as many as will take it. `sessionStorage` earns its place in the middle: it
// has its own quota, so it survives localStorage merely being full, and it
// survives a reload of the same tab.
//
// Reads then take the NEWEST value any tier holds rather than trusting a tier
// ordering. Tier order is the wrong rule and quietly reintroduces the bug this
// module exists to prevent: when a write is rejected, the rejecting tier KEEPS
// whatever older value it already had, so preferring it hands back a stale
// timestamp, an expired cooldown reads as no cooldown, and the message goes
// twice. Recency is the only ordering that is right in every direction - a
// newer value from another tab, a stale value a full localStorage is still
// holding, a tier that evicted the key from under us.
//
// The memory tier does NOT survive a reload, and reloading is a natural
// reaction to a request that just hung for ten seconds. When storage is denied
// outright - a sandboxed iframe denies both tiers - a reload inside the cooldown
// window can still let a duplicate through. That is the residual gap; there is
// no client-side tier left to close it, so it is stated here rather than
// papered over.
//
// Values are millisecond timestamps, which is what makes "newest" comparable.
const memoryFallback = new Map<string, string>();

/** Keys already reported as unpersisted, so the warning fires once per key. */
const reportedUnpersisted = new Set<string>();

function readTier(tier: 'local' | 'session', key: string): string | null {
  try {
    return (tier === 'local' ? localStorage : sessionStorage).getItem(key);
  } catch {
    return null;
  }
}

function writeTier(tier: 'local' | 'session', key: string, value: string): boolean {
  try {
    (tier === 'local' ? localStorage : sessionStorage).setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function safeGetItem(key: string): string | null {
  const candidates = [
    readTier('local', key),
    readTier('session', key),
    memoryFallback.get(key) ?? null,
  ].filter((value): value is string => value !== null);

  if (candidates.length === 0) return null;

  let newest: string | null = null;
  let newestMs = -Infinity;
  for (const candidate of candidates) {
    const ms = parseInt(candidate, 10);
    if (Number.isNaN(ms) || ms <= newestMs) continue;
    newestMs = ms;
    newest = candidate;
  }

  // Nothing parsed as a timestamp. Hand back the most durable value anyway so
  // the callers below can report it as unusable rather than as absent.
  return newest ?? candidates[0];
}

export function safeSetItem(key: string, value: string): void {
  // Written first and unconditionally, so the value survives however the
  // storage writes below turn out.
  memoryFallback.set(key, value);

  // Both are attempted, not just the first that takes it: a value in two places
  // is a value that survives either one being cleared, and the read resolves by
  // recency so a duplicate can never disagree with itself.
  const inLocal = writeTier('local', key, value);
  const inSession = writeTier('session', key, value);

  if (!inLocal && !inSession) {
    // Otherwise a site where every write is denied is indistinguishable, in
    // logs and in analytics, from one where storage works - and the cooldown is
    // quietly reduced to page lifetime.
    if (!reportedUnpersisted.has(key)) {
      reportedUnpersisted.add(key);
      console.warn(
        `Web Storage rejected every write for "${key}" - the send cooldown now lasts only as long as this page.`,
      );
    }
  } else {
    reportedUnpersisted.delete(key);
  }
}

/** Test seam: drops the module state so cases cannot leak between tests. */
export function clearMemoryFallback(): void {
  memoryFallback.clear();
  reportedUnpersisted.clear();
  reportedUnusable.clear();
}

/** Raw values already reported as unusable, so each warns only once. */
const reportedUnusable = new Set<string>();

/**
 * Age of a stored timestamp in ms, or `null` when it carries no usable signal.
 *
 * The two unusable shapes are treated differently, because only one of them
 * leaves a window to fall back on.
 *
 * A future timestamp means the clock moved backwards between the write and the
 * read, and how far into the future decides what to do about it:
 *
 * - Within one window: the realistic case, a small NTP correction or a
 *   sub-second skew landing between the submit and the retry. Clamped to age 0,
 *   a cooldown that has only just started. Failing open here disabled the guard
 *   in the one place it is a correctness mechanism rather than an anti-spam
 *   nicety - after a timeout it is all that stands between a retry and the same
 *   message being delivered twice.
 * - Further ahead than one window: fails open. Clamping unconditionally would
 *   NOT cost "at most one window": every later submit re-reads the same future
 *   timestamp, re-clamps to 0, and is refused again, so a device whose clock was
 *   hours or days ahead when the value was written would find both forms
 *   unusable until wall-clock time caught up - with nothing on screen but "please
 *   wait a moment". A skew that large is a broken clock, not the write/read race
 *   this guard is for, and a dead contact form is a worse failure than a possible
 *   duplicate send.
 *
 * NaN also fails open: a corrupted string says nothing about when - or whether -
 * anything was ever sent, so there is no window to clamp it to, and a value
 * nobody can interpret must not lock the form.
 *
 * Every unusable value warns. Failing open disables the duplicate-send guard
 * just as completely as a dropped write does, and the write path already warns
 * about that, so staying silent on this half would report the same invariant two
 * different ways.
 */
function warnUnusable(raw: string, problem: string): void {
  if (reportedUnusable.has(raw)) return;
  reportedUnusable.add(raw);
  console.warn(`Stored send cooldown "${raw}" ${problem}.`);
}

function elapsedSinceSubmit(
  lastSubmitRaw: string | null,
  now: number,
  windowSeconds: number,
): number | null {
  if (!lastSubmitRaw) return null;
  const lastSubmit = parseInt(lastSubmitRaw, 10);
  if (Number.isNaN(lastSubmit)) {
    warnUnusable(lastSubmitRaw, 'is not a number - duplicate sends are not being blocked');
    return null;
  }
  if (lastSubmit > now) {
    if (lastSubmit - now > windowSeconds * 1000) {
      warnUnusable(
        lastSubmitRaw,
        'is further ahead than the cooldown window, so the clock is wrong rather than merely skewed - ignoring it instead of wedging the form shut',
      );
      return null;
    }
    warnUnusable(
      lastSubmitRaw,
      'is in the future, so the clock moved backwards - treating it as a cooldown that just started',
    );
    return 0;
  }
  return now - lastSubmit;
}

export function isRateLimited(
  lastSubmitRaw: string | null,
  now: number,
  windowSeconds: number,
): boolean {
  const elapsed = elapsedSinceSubmit(lastSubmitRaw, now, windowSeconds);
  if (elapsed === null) return false;
  return elapsed / 1000 < windowSeconds;
}

export function remainingCooldownMs(
  lastSubmitRaw: string | null,
  now: number,
  windowSeconds: number,
): number {
  const elapsed = elapsedSinceSubmit(lastSubmitRaw, now, windowSeconds);
  if (elapsed === null) return 0;
  return Math.max(0, windowSeconds * 1000 - elapsed);
}
